import { url, tokenRef } from "api";

// ==================== HELPERS ====================
const getHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
  };

  const userToken = localStorage.getItem(tokenRef);
  if (userToken) {
    headers["Authorization"] = `Bearer ${userToken}`;
  }

  return headers;
};

const handleResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.err || "Request failed");
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

// ==================== PARTICIPANTS ====================

/**
 * Get participant rows for a project
 */
export const getParticipants = async (projectId) => {
  const response = await fetch(`${url}/projects/${projectId}/participants`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Get the grouped roster (owner company first, then partner companies
 * with their users): [{companyId, companyName, role, trades, color, users}]
 */
export const getParticipantsRoster = async (projectId) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/roster`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Search companies to add as partners (owner-side only; 403 otherwise)
 */
/**
 * The two lists the owner asked for: the deliverable recipients who are on a
 * company roster, and the ones who are on none - the refusals of the
 * automatic domain placement, landing somewhere a human looks.
 * Owner-company only, so partners get a 403 rather than another firm's
 * guest list.
 */
export const getRecipientRoster = async (projectId) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/recipient-roster`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

export const searchCompanies = async (projectId, q) => {
  const response = await fetch(
    `${url}/projects/${projectId}/participants/company-search?q=${encodeURIComponent(q || "")}`,
    {
      method: "GET",
      headers: getHeaders(),
    }
  );
  return handleResponse(response);
};

/**
 * The partner organisations this company already works with - picking one
 * in "Add partner" brings its people from the company's other projects.
 * -> { orgs: [{ companyId, name, projects, people, role, trades, color, onThisProject }] }
 */
export const getPartnerOrgs = async (projectId) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/partner-orgs`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Add a partner company to a project
 * body: {companyId, role?, trades?[], color?}
 */
export const addParticipant = async (projectId, body) => {
  const response = await fetch(`${url}/projects/${projectId}/participants`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * Hourly rates for a partner on the project (owner / company admins only):
 * body: { hourlyRate?, users?: [{ email, hourlyRate }], supervisors?: [{ email, hourlyRate }] }
 * A rate of null / "" clears it. The cost of hours spent on issues uses the
 * person's own rate, then the company's (back/utils/issueHours).
 */
export const setParticipantRates = async (projectId, pid, body) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/${pid}/rates`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * Update a participant row
 * body: {role?, trades?, color?, status?, excludedUserIds?}
 */
export const updateParticipant = async (projectId, pid, body) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/${pid}`, {
    method: "PATCH",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * The partner profile (owner, 2026-10-15): short code, the trade in the
 * owner's words, main / secondary contact, and an email for a person listed
 * by name only. Sends nobody anything (back/routes/participantProfile).
 * body: { shortCode?, tradeLabel?, trades?, addTradesFromLabel?,
 *         mainContact?: {name, email}, secondaryContact?: {name, email},
 *         people?: [{ key, name, email }] }
 * Returns the row with `missing` recomputed.
 */
export const updateParticipantProfile = async (projectId, pid, body) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/${pid}/profile`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * Delegated roster: a SUPERVISOR of a partner company adds one of their
 * own crew, allowed only when the owner switched on allowSupervisorInvite
 * for that company (403 otherwise).
 * body: {name, email, role?}
 */
export const addCrewMember = async (projectId, pid, body) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/${pid}/crew`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * ADMIN: move one person from this participant to another contractor on
 * the project. Re-files their BidLight account - which is what the
 * responsible dropdowns are grouped by - as well as the declared roster
 * entry, and optionally carries their assignments across.
 * body: {userId?, email?, toCompanyId, moveAssignments?}
 * -> {movedAccount, movedRosterEntry, checklistItems, issues, planItems, skipped[]}
 */
export const moveParticipantUser = async (projectId, pid, body) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/${pid}/move-user`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * Remove a participant row
 */
export const removeParticipant = async (projectId, pid) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/${pid}`, {
    method: "DELETE",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Projects this person may copy partners FROM - newest first, each with a
 * partner count. Gated on the server independently of the button that
 * opens it: hiding a control is presentation, not access control.
 */
export const getCopySources = async (projectId) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/copy-sources`, {
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Copy chosen partners from another project.
 *
 * A COLD COPY: rows, people, both rates, allowSupervisorInvite and each
 * person's existing `invited` state travel exactly as they are, and
 * NOTHING IS SENT. Inviting the copied people is a separate, deliberate
 * act with its own button - because the normal add path would have mailed
 * every person copied.
 */
export const copyParticipants = async (projectId, fromProjectId, companyIds) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/copy`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ fromProjectId, companyIds }),
  });
  return handleResponse(response);
};

/**
 * Invite the people on a partner row who have no account yet.
 *
 * Offered by ACCOUNT rather than by the `invited` flag: that flag records
 * an email about a PARTICULAR project, so on a copied row it can claim one
 * this project never sent. This is the only call here that sends mail.
 */
export const inviteParticipants = async (projectId, pid, emails) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/${pid}/invite`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ emails }),
  });
  return handleResponse(response);
};

// ==================== ROSTER MARKS (owner brief 2026-10-11 T43/T44) ====================

/**
 * The owning company's roster entries with their Executive mark and job
 * title, the job title list, and what the caller may change.
 */
export const getRosterMarks = async (projectId) => {
  const response = await fetch(`${url}/projects/${projectId}/roster-marks`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/** Owner side: { isExecutive?, jobTitle? } for one roster member. */
export const setOwnerRosterMark = async (projectId, userId, body) => {
  const response = await fetch(`${url}/projects/${projectId}/roster-marks/owner/${userId}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/** Partner side: one person's job title on a partner row ('' clears it). */
export const setPartnerJobTitle = async (projectId, pid, email, jobTitle) => {
  const response = await fetch(`${url}/projects/${projectId}/roster-marks/partner/${pid}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ email, jobTitle }),
  });
  return handleResponse(response);
};

// "ADD FROM <PARTNER>'S PEOPLE" (owner, 2026-10-15: "the partner core will
// combine users from all projects but only show specific people on specific
// projects"). The partner's people on the company's other projects who are
// not on this row; `includeRemoved` also lists people taken off this project.
export const getPartnerCore = async (projectId, pid, { includeRemoved = false } = {}) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/${pid}/core${includeRemoved ? "?includeRemoved=1" : ""}`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/** Add the picked ones: people = [{ key, as: "user"|"supervisor", title? }]. */
export const addFromPartnerCore = async (projectId, pid, people) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/${pid}/core`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ people }),
  });
  return handleResponse(response);
};

/**
 * One roster person's access from THIS project's Partners view (owner,
 * 2026-10-16). The project's owner side may set it (requireOwnerEdit), from
 * utils/partnerAccessKeys only; saving creates an account for a person who
 * has none, and emails nobody. These are account grants - they hold on every
 * project the person is on.
 * GET returns { email, name, hasAccount, fullAccount, keys, groups, labels, standard }.
 */
export const getRosterAccess = async (projectId, pid, email) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/${pid}/access?email=${encodeURIComponent(email)}`, {
    headers: getHeaders(),
  });
  return handleResponse(response);
};
export const setRosterAccess = async (projectId, pid, email, keys) => {
  const response = await fetch(`${url}/projects/${projectId}/participants/${pid}/access`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ email, keys }),
  });
  return handleResponse(response);
};
